import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Device } from '@capacitor/device';

import { Capacitor } from '@capacitor/core';

declare const InstallTrigger: any;
/* tslint:disable */
@Injectable()
export class PlatformService {
  private isDevicePlatform;
  private cachedDeviceId: string;
  private browserTypes = {
    Opera:
      (!!(<any>window).opr && !!(<any>window).opr.addons) ||
      !!(<any>window).opera ||
      navigator.userAgent.indexOf(' OPR/') >= 0,
    Firefox: typeof InstallTrigger !== 'undefined',
    Safari:
      /constructor/i.test((<any>window).HTMLElement) ||
      // eslint-disable-next-line func-names
      (function (p) {
        return p.toString() === '[object SafariRemoteNotification]';
      }(!(<any>window).safari || (<any>window).safari.pushNotification)),
    IE: /* @cc_on!@ */ !!document.DOCUMENT_NODE,
    Chrome:
      (!!(<any>window).chrome && !!(<any>window).chrome.webstore) ||
      (!!(<any>window).chrome && !!(<any>window).chrome.app),
    Edge: /Edge/.test(navigator.userAgent),
  };

  constructor(private platform: Platform) {
    this.checkPlatform();
    if (Capacitor.isNativePlatform()) {
      this.cachedDeviceId = localStorage.getItem('_deviceId') ?? undefined;
      Device.getId().then(({ identifier }) => {
        this.cachedDeviceId = identifier;
        localStorage.setItem('_deviceId', identifier);
      });
    }
  }

  isMobile(): boolean {
    return this.platform.is('mobile');
  }

  checkPlatform(): void {
    this.isDevicePlatform = this.platform.is('cordova');
  }

  isCordova(): boolean {
    return this.platform.is('cordova');
  }

  isTablet(): boolean {
    return this.platform.is('tablet') || this.platform.is('ipad');
  }

  isMobileWeb() {
    return this.platform.is('mobileweb');
  }

  isIosBrowser(): boolean {
    return !this.isDevicePlatform && this.isIos() && this.isMobile();
  }

  isIosApp(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  }

  isDesktop(): boolean {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator?.userAgent);
    return Capacitor.getPlatform() === 'web' && !isMobile;
  }

  isIos(): boolean {
    return this.platform.is('ios');
  }

  isLaptopWithTouch(): boolean {
    return this.platform.is('mobile') && !this.platform.is('android') && !this.platform.is('ios');
  }

  getDeviceId(): string {
    return this.cachedDeviceId ?? `${window.navigator.userAgent}`;
  }

  async getDeviceIdAsync(): Promise<string> {
    if (!Capacitor.isNativePlatform()) {
      return `${window.navigator.userAgent}`;
    }
    if (this.cachedDeviceId) {
      return this.cachedDeviceId;
    }
    const { identifier } = await Device.getId();
    this.cachedDeviceId = identifier;
    localStorage.setItem('_deviceId', identifier);
    return identifier;
  }

  getDeviceName(): string {
    return Object.keys(this.browserTypes).find((key) => this.browserTypes[key]) || 'Browser';
  }

  isBrowser(): boolean {
    const browserName = Object.keys(this.browserTypes).find((key) => this.browserTypes[key]);
    const availableBrowsers = ['Opera', 'Firefox', 'Safari', 'IE', 'Chrome', 'Browser'];

    return availableBrowsers.includes(browserName);
  }

  isEdge(): boolean {
    return this.browserTypes.Edge;
  }

  /**
   * returns emitter of native back button taps of Android and Windows devices
   */
  getBackButtonEmitter(): any | null {
    let result: any = null;
    if (this.isCordova() && !!this.platform.backButton) {
      result = this.platform.backButton;
    }
    return result;
  }

  checkForInstalledApp(): void {
    if (this.isCordova()) {
      return;
    }
    const path = `${window.location.pathname}${window.location.search}`;
    if (
      this.platform.is('mobile') &&
      (path.includes('first-time-user-setup') || path.includes('reset-password'))
    ) {
      // in case app is not installed
      const marketUrl = this.isIos()
        ? 'itms-apps://apps.apple.com/app/sony-music-promo-portal/id1466205488'
        : 'market://details?id=com.sonymusic.promoportal';
      setTimeout(() => window.location.assign(marketUrl), 25);
      // attempt to open installed app
      const url = `promoportal://app${path}`;
      window.location.assign(url);
    }
  }

  isAndroidMobileBrowser() {
    return !this.isCordova() && this.platform.is('mobile') && this.platform.is('android');
  }

  isAndroid() {
    return this.platform.is('android') && !this.platform.is('mobileweb');
  }

  isIOSMobileBrowser() {
    return !this.isCordova() && this.platform.is('mobile') && this.platform.is('ios');
  }
}
